import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useFormStore from '../state/useFormStore';
import { apiService } from '../utils/api';

interface WithAuthAndDataProps {
  children: React.ReactNode;
}

const generateDeviceId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const withAuthAndData = (WrappedComponent: React.ComponentType<any>) => {
  return (props: any) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const hash = useFormStore((state) => state.hash) || localStorage.getItem("hash");
    
    // Obtener todos los setters del store
    const {
      setHash,
      setNames,
      setCuilParameter,
      setEmploymentParameter,
      setDniData,
      setEmailData,
      setPhoneData,
      setFirstNameData,
      setLastNameData,
      setGenderData,
      setBankData,
      setBirthdateData,
      setRequestUid,
      setPreOffers,
      setToken,
      token
    } = useFormStore((state) => ({
      setHash: state.setHash,
      setNames: state.setNames,
      setCuilParameter: state.setCuilParameter,
      setEmploymentParameter: state.setEmploymentParameter,
      setDniData: state.setDniData,
      setEmailData: state.setEmailData,
      setPhoneData: state.setPhoneData,
      setFirstNameData: state.setFirstNameData,
      setLastNameData: state.setLastNameData,
      setGenderData: state.setGenderData,
      setBankData: state.setBankData,
      setBirthdateData: state.setBirthdateData,
      setRequestUid: state.setRequestUid,
      setPreOffers: state.setPreOffers,
      setToken: state.setToken,
      token: state.token
    }));

    useEffect(() => {
      const initializeData = async () => {
        // Evitar inicialización duplicada
        if (isInitialized) return;
        
        try {
          // 1. Verificar y obtener token
          if (!token) {
            const storedToken = localStorage.getItem('auth_token');
            if (storedToken) {
              setToken(storedToken);
            } else {
              const response = await apiService.auth.createSession();
              if (response.data?.data?.accessToken) {
                const newToken = response.data.data.accessToken;
                setToken(newToken);
                localStorage.setItem('auth_token', newToken);
              }
            }
          }

          // 2. Cargar datos del usuario si existe hash
          if (hash) {
            const storedData = localStorage.getItem(`prestix_${hash}`);
            if (storedData) {
              const data = JSON.parse(storedData);
              
              // Verificar si ya existe un deviceId
              if (!data.deviceId) {
                // Generar y guardar deviceId si no existe
                const deviceId = generateDeviceId();
                localStorage.setItem('deviceId', deviceId);
                data.deviceId = deviceId;
                localStorage.setItem(`prestix_${hash}`, JSON.stringify(data));
              }
              
              // Cargar datos del usuario
              if (data.name) setNames(data.name);
              if (data.cuit) setCuilParameter(data.cuit);
              if (data.employment) setEmploymentParameter(data.employment);
              if (data.dni) setDniData(data.dni);
              if (data.email) setEmailData(data.email);
              if (data.phone) setPhoneData(data.phone);
              if (data.first_name) setFirstNameData(data.first_name);
              if (data.last_name) setLastNameData(data.last_name);
              if (data.gender) setGenderData(data.gender);
              if (data.bank) setBankData(data.bank);
              if (data.birthdate) setBirthdateData(data.birthdate);
              if (data.requestUid) setRequestUid(data.requestUid);
              if (data.hash) setHash(data.hash);
              if (data.preOffers) setPreOffers(data.preOffers);
            } else {
              // Si no hay datos y no estamos en la página de inicio, redirigir
              if (window.location.pathname !== '/') {
                navigate('/');
                return;
              }
            }
          }else{
            // Check if we are in the signature page
            if(window.location.toString().includes('firma')){
              const requestUid = window.location.pathname.split('/')[1];
              const response = await apiService.requests.getStatus(requestUid);
              // Check  response code 
              if(response.status !== 200){
                navigate('/expirado');
                return;
              }
              // Check if can continue
              if(!response.data.requestStatus.statusDetail.canContinue || !response.data.requestStatus.uid){
                navigate('/expirado');
                return;
              }

              // The request can continue
              if (response.data.requestStatus.uid) {
                setRequestUid(response.data.requestStatus.uid);
                setHash(response.data.requestStatus.uid);
                localStorage.setItem('hash', response.data.requestStatus.uid);
                localStorage.setItem(`prestix_${response.data.requestStatus.uid}`, JSON.stringify({
                  requestUid: response.data.requestStatus.uid,
                  hash: response.data.requestStatus.uid,
                }));
              }
            }
          }
        } catch (error) {
          console.error('Error al inicializar datos:', error);
          if (window.location.pathname !== '/') {
            navigate('/');
          }
        } finally {
          setIsLoading(false);
          setIsInitialized(true);
        }
      };

      initializeData();
    }, [hash, navigate, setHash, setNames, setCuilParameter, setEmploymentParameter, 
        setDniData, setEmailData, setPhoneData, setFirstNameData, setLastNameData, 
        setGenderData, setBankData, setBirthdateData, setRequestUid, setPreOffers, 
        setToken, token, isInitialized]);

    if (isLoading) {
      return <div>Cargando...</div>; // O tu componente de loading
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAuthAndData; 